import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import config from 'config'

import editor from 'shared/editor/utils'
import { buildId } from 'util/globalId'
import { isHtmlEmpty } from 'util/strings'

import { threeStars as ThreeStars } from 'assets/icons/groove/v2'

import { selectHasHelplyCopilotAddon } from 'ducks/accounts/selectors/selectHasHelplyCopilotAddon'
import { selectAccountSubdomain } from 'ducks/accounts/selectors/selectAccountSubdomain'
import { selectCurrentTicketId } from 'ducks/tickets/selectors/selectCurrentTicketId'
import { selectCurrentMailboxId } from 'ducks/mailboxes/selectors/selectCurrentMailboxId'
import { selectCurrentChannelById } from 'ducks/channels/selectors'
import { selectCurrentConversationById } from 'ducks/tickets/selectors'
import { selectConversationEventGroupsByConversationId } from 'ducks/tickets/selectors/selectConversationEventGroupsByConversationId'
import { selectTicketRequestByConversationId } from 'ducks/tickets/selectors/selectTicketRequestByConversationId'
import { selectCurrentUser } from 'ducks/currentUser/selectors/selectCurrentUser'
import { selectCurrentUserGlobalId } from 'ducks/currentUser/selectors/selectCurrentUserGlobalId'
import { selectCurrentContact } from 'ducks/crm/contacts/selectors/current'
import { selectDraftByTicketId } from 'ducks/drafts2/selectors'
import { selectHelplyAccountId } from 'ducks/accounts/selectors/selectHelplyAccountId'
import { selectIsInNoteMode } from 'selectors/location'
import { selectIsRightSidebarCollapsed } from 'selectors/app/selectIsRightSidebarCollapsed'
import { selectIsHelplyCopilotOpen } from 'selectors/app/selectIsHelplyCopilotOpen'
import { selectHelplyCopilotDraftNonce } from 'selectors/app/selectHelplyCopilotDraftNonce'

import {
  doToggleRightSidebar,
  doToggleHelplyCopilot,
  doSetHelplyCopilotOpen,
} from 'actions/app'

import {
  doInsertHelplyCopilotText,
  doFetchHelplyCopilotGrant,
  doSetHelplyCopilotEditor,
} from './operations'
import { styles } from './styles'

function getHelplyOrigin() {
  try {
    return new URL(config.helplyCopilotUrl).origin
  } catch (e) {
    return null
  }
}

export default function HelplyCopilotPanel() {
  const dispatch = useDispatch()
  const hasCopilotAddon = useSelector(selectHasHelplyCopilotAddon)
  const helplyAccountId = useSelector(selectHelplyAccountId)
  const shouldUseCopilot = !!(helplyAccountId && hasCopilotAddon)
  const subdomain = useSelector(selectAccountSubdomain)
  const ticketId = useSelector(selectCurrentTicketId)
  // The current inbox's channel id, used to scope copilot ticket search to this
  // inbox. selectCurrentMailboxId returns the raw id; re-add the `ch_` prefix
  // (Channel global id) to match how Groove tickets are indexed in Qdrant.
  const channelId = useSelector(state =>
    buildId('Channel', selectCurrentMailboxId(state))
  )
  // Groove appends the inbox's signature to sent replies, so when the current
  // inbox has a (non-empty) signature we tell the copilot to skip its draft
  // sign-off — otherwise the agent's name would appear twice. Select the raw
  // signature (cheap, stable) and derive emptiness in a memo so the HTML strip
  // doesn't run on every unrelated store update.
  const signature = useSelector(state => {
    const channel = selectCurrentChannelById(
      state,
      selectCurrentMailboxId(state)
    )
    return (channel && channel.signature) || ''
  })
  const hasSignature = useMemo(() => !isHtmlEmpty(signature), [signature])
  const conversation = useSelector(
    state => (ticketId ? selectCurrentConversationById(state, ticketId) : null)
  )
  const eventGroups = useSelector(
    state =>
      ticketId
        ? selectConversationEventGroupsByConversationId(state, ticketId)
        : null
  )

  const isTicketLoaded = useSelector(
    state =>
      ticketId
        ? selectTicketRequestByConversationId(state, ticketId).loaded
        : false
  )
  // A real, fully-loaded ticket — not the `/tickets/new` placeholder (`ticketId`
  // is 'new' there) and not still loading. Gates the launcher, drawer, and the
  // context/insert actions so the copilot never acts on a half-loaded or
  // non-ticket route. The iframe itself stays mounted regardless (see the render
  // gate) to avoid a re-auth on transient ticket switches.
  const isTicketReady = !!ticketId && ticketId !== 'new' && isTicketLoaded
  // Read inside the iframe message handler (insert/set-editor) without making the
  // listener re-subscribe on every readiness change.
  const isTicketReadyRef = useRef(isTicketReady)
  isTicketReadyRef.current = isTicketReady
  const currentUser = useSelector(selectCurrentUser)
  const contact = useSelector(selectCurrentContact)
  const isNoteMode = useSelector(selectIsInNoteMode)
  const isRightSidebarCollapsed = useSelector(selectIsRightSidebarCollapsed)
  const replyDraft = useSelector(
    state => (ticketId ? selectDraftByTicketId(state, ticketId, 'reply') : null)
  )
  const noteDraft = useSelector(
    state => (ticketId ? selectDraftByTicketId(state, ticketId, 'note') : null)
  )
  // Mirror whichever composer is active so Format reflects what the agent is
  // editing (reply or note) — otherwise Format stays disabled while writing a note.
  const activeDraft = isNoteMode ? noteDraft : replyDraft

  // Open state lives in Redux so the conversation keyboard shortcut (and any
  // other global trigger) can toggle the drawer, mirroring the right sidebar.
  const isOpen = useSelector(selectIsHelplyCopilotOpen)
  // A draft requested from outside the panel (the AI Draft button / `d` hotkey
  // when the copilot addon is on). doRequestHelplyCopilotDraft opens the drawer
  // and bumps this nonce; we forward a `draft` command into the iframe below.
  const draftNonce = useSelector(selectHelplyCopilotDraftNonce)
  // Once opened, keep the iframe mounted (just hidden by the collapsed drawer)
  // so it doesn't re-authenticate and flash the loading skeleton on every
  // reopen — Zendesk's sidebar iframe is likewise persistent.
  const [hasOpened, setHasOpened] = useState(false)
  const iframeRef = useRef(null)
  // Tracks the previous open state so we can detect reopen transitions.
  const wasOpenRef = useRef(false)
  // iframe boot state + a draft requested before boot finished, so a cold-open
  // draft is flushed once the iframe signals `ready`.
  const iframeReadyRef = useRef(false)
  const pendingDraftRef = useRef(false)
  const prevDraftNonceRef = useRef(draftNonce)
  // Mirror the right-sidebar collapse state so the open effect reads it at the
  // moment the copilot opens, without re-running (and re-opening) if the agent
  // later collapses it themselves.
  const isRightSidebarCollapsedRef = useRef(isRightSidebarCollapsed)
  isRightSidebarCollapsedRef.current = isRightSidebarCollapsed
  // Whether the right sidebar was auto-opened for the copilot (vs already open
  // by the agent's own choice). Lets the close path restore the stored pref
  // instead of leaving the sidebar open.
  const didAutoOpenRightSidebarRef = useRef(false)
  const helplyOrigin = useMemo(getHelplyOrigin, [])

  const agentName = useMemo(
    () => {
      if (!currentUser) return null
      const full = [currentUser.first_name, currentUser.last_name]
        .filter(Boolean)
        .join(' ')
        .trim()
      return currentUser.name || full || currentUser.email || null
    },
    [currentUser]
  )

  // The agent's gid (`ag_<id>` — same format the GraphQL v2 API returns for
  // Agent.id, which Helply's ticket index stores as the message author id).
  // Lets the copilot match this agent's own past replies exactly.
  const agentId = useSelector(selectCurrentUserGlobalId) || null

  const ticketContext = useMemo(
    () => {
      if (!ticketId) return null
      const comments = (eventGroups || [])
        .map(group => {
          // `group.summary` is the (possibly summary-only) message. The full
          // HTML body is present once a group is fully loaded; groups loaded as
          // summaries carry only the plain text on `bodyPlainText`, so fall back
          // to it — otherwise the comment is filtered out as empty below and the
          // AI loses that slice of the conversation.
          const body =
            (group && group.summary && group.summary.body) ||
            (group && group.summary && group.summary.bodyPlainText) ||
            (group && group.body) ||
            ''
          // The message author lives on `summary.author` (a Contact/Agent with
          // a real name); `actor` is the secondary fallback for system events.
          const authorName =
            (group &&
              group.summary &&
              group.summary.author &&
              group.summary.author.name) ||
            (group && group.actor && group.actor.name) ||
            'Unknown'
          const createdAt =
            (group && group.summary && group.summary.createdAt) ||
            (group && group.createdAt) ||
            new Date().toISOString()
          return {
            author: { name: authorName },
            value: body,
            created_at: createdAt,
            public: !(group && group.isNote),
          }
        })
        .filter(comment => comment.value)

      // The AI only reads subject/description/comments, and Groove's per-event
      // author names arrive as "Unknown", so surface the contact explicitly by
      // prepending it to the description (name + email when available).
      const contactName = (contact && contact.name) || ''
      const contactEmail = (contact && contact.email) || ''
      const contactLine =
        contactName || contactEmail
          ? `Customer: ${[contactName, contactEmail && `<${contactEmail}>`]
              .filter(Boolean)
              .join(' ')}`
          : ''
      const firstMessage = (comments[0] && comments[0].value) || ''

      return {
        id: ticketId,
        subject: (conversation && conversation.subject) || '',
        description: [contactLine, firstMessage].filter(Boolean).join('\n\n'),
        comments,
      }
    },
    [ticketId, conversation, eventGroups, contact]
  )

  const postToIframe = useCallback(
    message => {
      const win = iframeRef.current && iframeRef.current.contentWindow
      if (win && helplyOrigin) {
        win.postMessage(message, helplyOrigin)
      }
    },
    [helplyOrigin]
  )

  const sendInit = useCallback(
    () => {
      if (!subdomain) return
      postToIframe({
        source: 'groove',
        type: 'init',
        payload: {
          subdomain,
          agentName,
          agentId,
          colorScheme: 'light',
        },
      })
    },
    [postToIframe, subdomain, agentName, agentId]
  )

  const sendContext = useCallback(
    () => {
      // Don't push half-loaded or non-ticket (`/tickets/new`) context.
      if (!isTicketReady) return
      postToIframe({
        source: 'groove',
        type: 'context',
        payload: {
          ticket: ticketContext,
          agentName,
          agentId,
          channelId,
          hasSignature,
        },
      })
    },
    [
      postToIframe,
      ticketContext,
      agentName,
      agentId,
      channelId,
      hasSignature,
      isTicketReady,
    ]
  )

  const sendEditor = useCallback(
    () => {
      if (!isTicketReady) return
      // Prefer the live composer content: the draft body is debounced, so right
      // after the agent types it can lag the editor (and Format would then run on
      // stale text). editor.getContent() returns the mounted editor's current
      // html, or false when the composer is collapsed — fall back to the draft then.
      let content = (activeDraft && activeDraft.body) || ''
      const live = editor.getContent()
      if (typeof live === 'string') content = live
      postToIframe({
        source: 'groove',
        type: 'editor',
        payload: { content },
      })
    },
    [postToIframe, activeDraft, isTicketReady]
  )

  const handleToggle = useCallback(() => dispatch(doToggleHelplyCopilot()), [
    dispatch,
  ])

  // Mount the iframe on first open and keep it mounted thereafter.
  useEffect(
    () => {
      if (isOpen) setHasOpened(true)
    },
    [isOpen]
  )

  // Refocus the copilot input whenever the drawer is reopened. The iframe stays
  // mounted across open/close, so its input only autofocuses on the first open;
  // subsequent reopens need an explicit nudge.
  useEffect(
    () => {
      const reopened = isOpen && !wasOpenRef.current && hasOpened
      wasOpenRef.current = isOpen
      if (reopened) {
        postToIframe({ source: 'groove', type: 'focus' })
      }
    },
    [isOpen, hasOpened, postToIframe]
  )

  // If the agent collapses the sidebar themselves while the copilot is open,
  // the auto-open no longer owns its state — any later expand is the agent's
  // own (persisted) preference, so closing the copilot must leave it alone.
  useEffect(
    () => {
      if (isOpen && isRightSidebarCollapsed) {
        didAutoOpenRightSidebarRef.current = false
      }
    },
    [isOpen, isRightSidebarCollapsed]
  )

  // When the copilot opens, open Groove's right sidebar (if collapsed) so the
  // conversation/editor reflows clear of the docked drawer instead of being
  // hidden behind it. Mirroring the forced panel width, the auto-open is
  // undone on close: if the sidebar was only opened for the copilot (and the
  // agent didn't toggle it meanwhile), collapse it back to the stored pref.
  useEffect(
    () => {
      if (isOpen) {
        if (isRightSidebarCollapsedRef.current) {
          didAutoOpenRightSidebarRef.current = true
          dispatch(doToggleRightSidebar())
        }
      } else if (didAutoOpenRightSidebarRef.current) {
        didAutoOpenRightSidebarRef.current = false
        if (!isRightSidebarCollapsedRef.current) {
          dispatch(doToggleRightSidebar())
        }
      }
    },
    [isOpen, dispatch]
  )

  // Handle messages coming back from the copilot iframe.
  useEffect(
    () => {
      // Don't listen until the addon is on AND we have a valid iframe origin to
      // validate messages against.
      if (!shouldUseCopilot || !helplyOrigin) return undefined

      const handleMessage = event => {
        // Only trust messages from our mounted iframe window at its exact
        // origin. If the iframe isn't mounted yet (no contentWindow) or either
        // check fails, drop the message rather than acting on a `source` tag
        // that any frame could forge.
        const win = iframeRef.current && iframeRef.current.contentWindow
        if (!win || event.source !== win || event.origin !== helplyOrigin) {
          return
        }

        const data = event.data
        if (
          !data ||
          typeof data !== 'object' ||
          data.source !== 'helply-copilot'
        ) {
          return
        }

        switch (data.type) {
          case 'ready':
            iframeReadyRef.current = true
            sendInit()
            sendContext()
            sendEditor()
            // Flush a draft requested while the iframe was still booting.
            if (pendingDraftRef.current) {
              pendingDraftRef.current = false
              postToIframe({ source: 'groove', type: 'draft' })
            }
            break
          case 'close':
            dispatch(doSetHelplyCopilotOpen(false))
            break
          case 'request-grant':
            // The iframe needs an account-scoped grant to authenticate. Mint
            // one via Groove's backend and hand it back. The thunk resolves
            // null on error/timeout, but never let a rejection leave the
            // iframe waiting out its own request-grant timer.
            dispatch(doFetchHelplyCopilotGrant())
              .catch(() => null)
              .then(grant => {
                postToIframe({
                  source: 'groove',
                  type: 'grant',
                  payload: { grant: grant || null },
                })
              })
            break
          case 'insert':
            // Ignore inserts unless a real, loaded ticket is active — otherwise a
            // late click could write into a half-loaded or `/tickets/new` draft.
            if (!isTicketReadyRef.current) break
            dispatch(
              doInsertHelplyCopilotText({
                html: data.payload && data.payload.html,
                asNote: !!(data.payload && data.payload.asNote),
              })
            )
            break
          case 'set-editor':
            if (!isTicketReadyRef.current) break
            dispatch(
              doSetHelplyCopilotEditor({
                html: data.payload && data.payload.html,
              })
            )
            break
          default:
            break
        }
      }

      window.addEventListener('message', handleMessage)
      return () => window.removeEventListener('message', handleMessage)
    },
    [
      shouldUseCopilot,
      helplyOrigin,
      sendInit,
      sendContext,
      sendEditor,
      postToIframe,
      dispatch,
    ]
  )

  // Keep the iframe in sync as the agent switches conversations / edits the draft.
  useEffect(
    () => {
      if (isOpen) sendContext()
    },
    [isOpen, sendContext]
  )

  useEffect(
    () => {
      if (isOpen) sendEditor()
    },
    [isOpen, sendEditor]
  )

  // Forward a requested draft into the iframe. If the iframe has already booted,
  // refresh context and send immediately; otherwise mark it pending so the
  // `ready` handler sends it once the copilot is up (cold open).
  useEffect(
    () => {
      if (draftNonce === prevDraftNonceRef.current) return
      prevDraftNonceRef.current = draftNonce
      if (!draftNonce) return
      if (iframeReadyRef.current) {
        sendContext()
        postToIframe({ source: 'groove', type: 'draft' })
      } else {
        pendingDraftRef.current = true
      }
    },
    [draftNonce, sendContext, postToIframe]
  )

  // Stable, account-level requirements — without these there's no copilot at
  // all for this account/agent. (`agentName` is sent to the copilot so the AI
  // can sign off replies; the per-agent rate limit keys on the agent id in the
  // signed grant, not this client-supplied name.)
  if (!config.helplyCopilotUrl || !shouldUseCopilot || !agentName) return null

  // First open requires a ready ticket so the AI isn't asked against a
  // half-loaded or placeholder (`/tickets/new`) conversation. After that we keep
  // the panel + iframe MOUNTED even when the ticket isn't ready — classic-view
  // ticket switches route through the no-ticket list, and unmounting would force
  // a re-auth. The launcher, drawer, and context/insert actions stay gated on
  // `isTicketReady` below, so a not-ready ticket just hides the UI.
  if (!hasOpened && !isTicketReady) return null

  const iframeSrc = `${config.helplyCopilotUrl.replace(
    /\/$/,
    ''
  )}/copilot/groove/index.html`

  return (
    <>
      {/* Not a ready ticket (classic-view list, a still-loading ticket, or
          `/tickets/new`): hide the launcher, but keep the iframe mounted below so
          it doesn't re-auth. */}
      {isTicketReady && (
        <button
          type="button"
          onClick={handleToggle}
          aria-label="Toggle Copilot"
          css={styles.toggleButton}
        >
          <ThreeStars />
        </button>
      )}
      <div css={styles.drawer(isOpen && isTicketReady)}>
        {(isOpen || hasOpened) && (
          <iframe
            ref={iframeRef}
            title="Copilot"
            src={iframeSrc}
            css={styles.iframe}
            allow="clipboard-write"
          />
        )}
      </div>
    </>
  )
}
